import { useCallback, useEffect, useState } from 'react';
import WorkIcon from '@mui/icons-material/Work';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { hrApi } from '@/api';
import { PageActionGate } from '@/components/auth/PageActionGate';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { showApiError } from '@/utils/apiError';
import { KeyValueTable } from '@/components/ui/KeyValueTable';
import { notify } from '@/core/toast/notify';
import { useCompanyScope } from '@/hooks/useCompanyScope';
import type { HrWorkforceSettings } from '@/types/hr';

const HIRING_MODE_OPTIONS = [
  {
    value: 'recruitment',
    label: 'Recruitment only (recommended)',
    description: 'New hires must go through HR → Recruitment → Hire. Blocks direct Add employee.',
  },
  {
    value: 'direct',
    label: 'Allow direct employee creation',
    description: 'HR can add employees from Administration → Employees without the ATS workflow.',
  },
] as const;

export function HrSetupPage() {
  const { selectedCompany, companyScopeVersion } = useCompanyScope();
  const [snapshot, setSnapshot] = useState<Record<string, unknown> | null>(null);
  const [hiringSettings, setHiringSettings] = useState<HrWorkforceSettings | null>(null);
  const [hiringMode, setHiringMode] = useState<'recruitment' | 'direct'>('recruitment');
  const [seeding, setSeeding] = useState(false);
  const [savingHiring, setSavingHiring] = useState(false);

  const load = useCallback(() => {
    hrApi.getSetup().then(setSnapshot).catch((err) => showApiError(err, 'Failed to load HR setup'));
    hrApi
      .getWorkforceSettings()
      .then((settings) => {
        setHiringSettings(settings);
        setHiringMode((settings.requireRecruitmentPath ?? true) ? 'recruitment' : 'direct');
      })
      .catch((err) => showApiError(err, 'Failed to load hiring policy'));
  }, []);

  useEffect(() => {
    load();
  }, [load, companyScopeVersion]);

  const seed = async () => {
    setSeeding(true);
    try {
      await hrApi.seedSetup();
      notify.success('Setup seeded successfully');
      load();
    } catch (err) {
      showApiError(err, 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const saveHiringPolicy = async () => {
    setSavingHiring(true);
    try {
      const saved = await hrApi.saveWorkforceSettings({
        requireRecruitmentPath: hiringMode === 'recruitment',
      });
      setHiringSettings(saved);
      setHiringMode(saved.requireRecruitmentPath ? 'recruitment' : 'direct');
      notify.success('Hiring policy saved for ' + (selectedCompany?.name ?? 'this company'));
    } catch (err) {
      showApiError(err, 'Failed to save hiring policy');
    } finally {
      setSavingHiring(false);
    }
  };

  const activeModeMeta = HIRING_MODE_OPTIONS.find((o) => o.value === hiringMode);

  return (
    <div className="space-y-6">
      <PageHeader
        title="HR Setup"
        description="Company-scoped HR configuration — switch company in the header to manage another legal entity"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'Setup' }]}
        actions={
          <PageActionGate action="edit">
            <Button onClick={seed} disabled={seeding}>
              {seeding ? 'Seeding…' : 'Seed defaults'}
            </Button>
          </PageActionGate>
        }
      />

      <section className="card p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Hiring policy</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Applies to{' '}
              <span className="font-medium text-slate-800 dark:text-slate-200">
                {selectedCompany?.name ?? 'the active company'}
              </span>
              {selectedCompany?.code ? ` (${selectedCompany.code})` : ''}. Each company can use a different rule.
            </p>
          </div>
          <PageActionGate action="edit">
            <Button onClick={saveHiringPolicy} disabled={savingHiring || !hiringSettings}>
              {savingHiring ? 'Saving…' : 'Save hiring policy'}
            </Button>
          </PageActionGate>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <div>
            <Select
              label="How new employees are created"
              value={hiringMode}
              onChange={(v) => setHiringMode(v as 'recruitment' | 'direct')}
              options={HIRING_MODE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            />
            {activeModeMeta && (
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{activeModeMeta.description}</p>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">End-to-end flow</p>
            {hiringMode === 'recruitment' ? (
              <ol className="mt-3 space-y-2 text-sm text-slate-700 dark:text-slate-200">
                <li className="flex gap-2">
                  <WorkIcon sx={{ fontSize: 18 }} className="mt-0.5 shrink-0 text-indigo-600" />
                  <span>
                    <strong>Recruitment path:</strong> HR → Recruitment → requisition → candidate → offer → preboarding
                    → <strong>Create employee</strong>
                  </span>
                </li>
                <li className="flex gap-2">
                  <PersonAddIcon sx={{ fontSize: 18 }} className="mt-0.5 shrink-0 text-rose-600" />
                  <span>
                    <strong>Blocked:</strong> Administration → Employees → Add employee (shows policy message)
                  </span>
                </li>
              </ol>
            ) : (
              <ol className="mt-3 space-y-2 text-sm text-slate-700 dark:text-slate-200">
                <li className="flex gap-2">
                  <PersonAddIcon sx={{ fontSize: 18 }} className="mt-0.5 shrink-0 text-emerald-600" />
                  <span>
                    <strong>Direct path:</strong> Administration → Employees → Add employee → complete HR profile in
                    Employee directory
                  </span>
                </li>
                <li className="flex gap-2">
                  <WorkIcon sx={{ fontSize: 18 }} className="mt-0.5 shrink-0 text-slate-500" />
                  <span>
                    <strong>Optional:</strong> You can still use Recruitment for external hiring; both paths coexist.
                  </span>
                </li>
              </ol>
            )}
          </div>
        </div>
      </section>

      <section className="card p-6">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">Setup snapshot</h2>
        {snapshot ? (
          <KeyValueTable data={snapshot} embedded emptyTitle="No setup data" />
        ) : (
          <p className="text-sm text-slate-500">Loading…</p>
        )}
      </section>
    </div>
  );
}
